/**
 * The data source, as the screen needs it.
 *
 * What the browser is allowed to know about a connection: the spreadsheet id
 * (not a secret, but not public either — readable only by members, and the
 * link to open it in Google is shown only with `datasource.manage`), the tab,
 * the column map, the status and the last few runs. It does not receive, and
 * cannot ask for, anything from `workspace_secrets`.
 *
 * Every write here goes through Row Level Security as the person doing it:
 * `workspace_integrations` is policed on `datasource.manage`, so a member who
 * may run a sync but not reconfigure it changes nothing, whatever this file
 * does.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_AMOUNT_MODEL, isAmountModel } from '@timely/domain';
import type { AmountModel, ColumnMap, IdentityStrategy, SheetSource } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { apiPost } from '../lib/api';

export interface Integration {
    spreadsheetId: string | null;
    sheetGid: string | null;
    sheetName: string | null;
    connectionType: 'export_csv' | 'published_csv' | 'api';
    identityStrategy: IdentityStrategy;
    /** How this company's sheet says which way an amount points. */
    amountModel: AmountModel;
    columns: ColumnMap;
    status: 'unconfigured' | 'ok' | 'error';
    statusDetail: string | null;
    lastCheckedAt: string | null;
}

export interface SyncRun {
    id: string;
    startedAt: string;
    finishedAt: string | null;
    result: 'ok' | 'failed' | 'cancelled' | null;
    counts: Record<string, number>;
    errorDetail: string | null;
}

const UNCONFIGURED: Integration = {
    spreadsheetId: null, sheetGid: null, sheetName: null,
    connectionType: 'export_csv', identityStrategy: 'source_key',
    amountModel: DEFAULT_AMOUNT_MODEL,
    columns: {}, status: 'unconfigured', statusDetail: null, lastCheckedAt: null,
};

interface IntegrationRow {
    spreadsheet_id: string | null;
    sheet_gid: string | null;
    sheet_name: string | null;
    connection_type: Integration['connectionType'];
    identity_strategy: IdentityStrategy;
    amount_model: string | null;
    config: { columns?: ColumnMap } | null;
    status: Integration['status'];
    status_detail: string | null;
    last_checked_at: string | null;
}

interface RunRow {
    id: string;
    started_at: string;
    finished_at: string | null;
    result: SyncRun['result'];
    counts: Record<string, number> | null;
    error_detail: string | null;
}

export function useDataSource(workspaceId: string) {
    const [integration, setIntegration] = useState<Integration>(UNCONFIGURED);
    const [runs, setRuns] = useState<SyncRun[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dropped on arrival if the company changed while it was in flight.
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);

        const client = supabase();
        const [integrationResult, runsResult] = await Promise.all([
            client.from('workspace_integrations')
                .select('spreadsheet_id, sheet_gid, sheet_name, connection_type, identity_strategy, amount_model, config, status, status_detail, last_checked_at')
                .eq('workspace_id', workspaceId).eq('kind', 'receivables').maybeSingle(),
            client.from('sync_runs')
                .select('id, started_at, finished_at, result, counts, error_detail')
                .eq('workspace_id', workspaceId).order('started_at', { ascending: false }).limit(10),
        ]);

        if (issuedFor.current !== workspaceId) return;

        if (integrationResult.error) {
            setError('We could not load your data source. Try again in a moment.');
            setLoading(false);
            return;
        }

        const row = integrationResult.data as unknown as IntegrationRow | null;
        setIntegration(row ? {
            spreadsheetId: row.spreadsheet_id,
            sheetGid: row.sheet_gid,
            sheetName: row.sheet_name,
            connectionType: row.connection_type,
            identityStrategy: row.identity_strategy,
            // An unrecognised value falls back rather than throwing: a row
            // written by a newer deployment must not blank the screen.
            amountModel: isAmountModel(row.amount_model) ? row.amount_model : DEFAULT_AMOUNT_MODEL,
            columns: row.config?.columns ?? {},
            status: row.status,
            statusDetail: row.status_detail,
            lastCheckedAt: row.last_checked_at,
        } : UNCONFIGURED);

        setRuns(((runsResult.data ?? []) as RunRow[]).map(r => ({
            id: r.id,
            startedAt: r.started_at,
            finishedAt: r.finished_at,
            result: r.result,
            counts: r.counts ?? {},
            errorDetail: r.error_detail,
        })));
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    return { integration, runs, loading, error, reload: load };
}

/* ------------------------------ the calls -------------------------------- */

export interface CheckResult {
    source: SheetSource;
    headers: string[];
    sample: string[][];
    rowCount: number;
    suggested: ColumnMap;
    /** The model the sheet's own columns hint at. A suggestion, not a choice. */
    suggestedModel: AmountModel;
    bytes: number;
}

/**
 * Read a pasted link without saving anything.
 *
 * The link goes to the server as a string and is parsed there. It is parsed
 * here too, before the call, so an obviously wrong paste is answered instantly
 * — but the browser's opinion is a courtesy, and the server's parse is the one
 * that decides.
 */
export const checkSheet = (workspaceId: string, link: string) =>
    apiPost<CheckResult>('/api/datasource/check', { workspaceId, link });

export interface SyncResult {
    applied: boolean;
    counts: {
        read: number; new: number; changed: number; unchanged: number;
        settled: number; settledAmount: number; excluded: number; problems: number;
        /** Accounts the source listed that the read refused. Left untouched. */
        heldBack?: number;
        inserted?: number; updated?: number;
    };
    problems: { rowNumber: number; kind: string; message: string }[];
    excluded: { rowNumber: number; label: string; reason: string }[];
}

export const runSync = (workspaceId: string, dryRun: boolean) =>
    apiPost<SyncResult>('/api/datasource/sync', { workspaceId, dryRun });

/**
 * Saving a connection.
 *
 * Only the parts. The pasted link never reaches the database — by the time this
 * is called it has already been reduced to a spreadsheet id and a tab id, and
 * the column map is a map of header names, not of anything fetchable.
 */
export async function saveConnection(
    workspaceId: string,
    source: SheetSource,
    columns: ColumnMap,
    identityStrategy: IdentityStrategy,
    amountModel: AmountModel,
    sheetName: string | null,
): Promise<void> {
    const { error } = await supabase()
        .from('workspace_integrations')
        .upsert({
            workspace_id: workspaceId,
            kind: 'receivables',
            provider: 'google_sheets',
            spreadsheet_id: source.spreadsheetId,
            sheet_gid: source.gid,
            sheet_name: sheetName,
            connection_type: source.connectionType,
            identity_strategy: identityStrategy,
            amount_model: amountModel,
            config: { columns },
            status: 'ok',
            status_detail: null,
            last_checked_at: new Date().toISOString(),
        }, { onConflict: 'workspace_id,kind' });

    if (error) {
        throw new Error(
            /row-level security|permission denied/i.test(error.message)
                ? 'You do not have permission to change this company’s data source.'
                : 'We could not save that connection. Try again in a moment.',
        );
    }
}

export async function disconnect(workspaceId: string): Promise<void> {
    const { error } = await supabase()
        .from('workspace_integrations')
        .delete().eq('workspace_id', workspaceId).eq('kind', 'receivables');
    if (error) throw new Error('We could not disconnect that sheet. Try again in a moment.');
}
